"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { portfolioBuilderApi } from "@/lib/api";
import { usePortfolioBuilderStore } from "@/stores/portfolioBuilderStore";
import type {
  InvestorProfileRequest,
  ApplyPortfolioRequest,
} from "@/types/portfolio-builder.types";

export function useRecommendation() {
  const { setRecommendation, setIsLoading, setError, setStep, setInsufficientCatalog } =
    usePortfolioBuilderStore();

  return useMutation({
    mutationFn: (profile: InvestorProfileRequest) =>
      portfolioBuilderApi.recommend(profile),
    onMutate: () => {
      setIsLoading(true);
      setError(null);
      setInsufficientCatalog(null);
    },
    onSuccess: (data) => {
      // SFWL-011: tenant's enabled+scoped catalog is too small for the
      // engine — not an error, a distinct empty state (RecommendationStep
      // renders it instead of results).
      if (data.status === "insufficient_catalog") {
        setRecommendation(null);
        setInsufficientCatalog({
          available: data.available,
          minRequired: data.minRequired,
        });
        setIsLoading(false);
        setStep(4);
        return;
      }
      setRecommendation(data);
      setIsLoading(false);
      setStep(4);
    },
    onError: (err: Error) => {
      setError(err.message);
      setIsLoading(false);
    },
  });
}

export function useApplyPortfolio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ApplyPortfolioRequest) => portfolioBuilderApi.apply(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
      queryClient.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}
